/**
 * Compliance Database
 * ------------------------------------------------------------
 * Admin-only page. Lets admins:
 *   • Create/rename/delete Sections (user-defined; starts at 0 sections)
 *   • Create/edit/delete Compliance Items within a section
 *   • Bulk-import items from CSV/XLSX
 *
 * Non-admin users see a read-only view (they get the tracker instead).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Upload, Download, ChevronRight, Layers, X, Loader, Paperclip } from 'lucide-react';
import { complianceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ComplianceItemModal from '../components/compliance/ComplianceItemModal';
import ComplianceDocsModal from '../components/compliance/ComplianceDocsModal';

const ComplianceDatabase = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({ name: '', description: '' });
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [docsTarget, setDocsTarget] = useState(null); // { level: 'section'|'item', entity, title }

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [secRes, itemRes] = await Promise.all([
        complianceAPI.listSections(),
        complianceAPI.listItems(),
      ]);
      setSections(secRes.data || []);
      setItems(itemRes.data || []);
      if (secRes.data.length && !selectedSection) {
        setSelectedSection(secRes.data[0].id);
      }
      return { sections: secRes.data || [], items: itemRes.data || [] };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredItems = selectedSection ? items.filter(i => i.section_id === selectedSection) : [];

  const handleSaveSection = async () => {
    if (!sectionForm.name.trim()) return;
    if (editingSection) {
      await complianceAPI.updateSection(editingSection.id, sectionForm);
    } else {
      await complianceAPI.createSection(sectionForm);
    }
    setShowSectionForm(false);
    setEditingSection(null);
    setSectionForm({ name: '', description: '' });
    await fetchAll();
  };

  const handleDeleteSection = async (sec) => {
    if (!window.confirm(`Delete section "${sec.name}"? This cannot be undone.`)) return;
    try {
      await complianceAPI.deleteSection(sec.id);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete section');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? All assignments and occurrences will be removed.`)) return;
    await complianceAPI.deleteItem(item.id);
    await fetchAll();
  };

  const handleDownloadTemplate = async () => {
    const res = await complianceAPI.downloadItemTemplate();
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compliance_items_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const res = await complianceAPI.bulkImportItems(importFile);
      setImportResult(res.data);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="compliance-database-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Compliance Database</h1>
          <p className="text-gray-600 mt-1">Manage compliance sections and line items — assign them to Funds from the fund's Compliance tab.</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/compliance"
            className="inline-flex items-center px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            data-testid="back-to-tracker"
          >
            Back to Tracker
          </Link>
          {isAdmin && (
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
              data-testid="download-item-template-btn"
            >
              <Download size={16} className="mr-2" /> CSV Template
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sections rail */}
        <aside className="md:col-span-1">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700 inline-flex items-center gap-2">
                <Layers size={14} /> Sections
              </h2>
              {isAdmin && (
                <button
                  onClick={() => { setEditingSection(null); setSectionForm({ name: '', description: '' }); setShowSectionForm(true); }}
                  className="text-blue-600 hover:text-blue-800"
                  data-testid="add-section-btn"
                  title="Add section"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
            {sections.length === 0 && (
              <div className="px-4 py-6 text-sm text-gray-500">
                No sections yet. {isAdmin && 'Click ＋ to create one (e.g. "SEBI AIF Compliance").'}
              </div>
            )}
            <ul>
              {sections.map(s => (
                <li key={s.id}>
                  <button
                    onClick={() => setSelectedSection(s.id)}
                    className={`w-full text-left px-4 py-3 flex items-center justify-between text-sm border-b hover:bg-blue-50 transition-colors ${selectedSection === s.id ? 'bg-blue-100 font-medium' : ''}`}
                    data-testid={`section-${s.id}`}
                  >
                    <span className="flex-1 truncate">{s.name}</span>
                    {(s.documents || []).length > 0 && (
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSection(s.id);
                          setDocsTarget({ level: 'section', entity: s, title: `Section: ${s.name}` });
                        }}
                        className="inline-flex items-center text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 mr-2 cursor-pointer hover:bg-indigo-100"
                        title={`View ${s.documents.length} reference document(s)`}
                        data-testid={`section-doc-count-${s.id}`}>
                        <Paperclip size={10} className="mr-0.5" />{s.documents.length}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 mr-2">
                      {items.filter(i => i.section_id === s.id).length}
                    </span>
                    <ChevronRight size={14} className="text-gray-400" />
                  </button>
                  {selectedSection === s.id && (
                    <div className="px-4 py-2 bg-blue-50/50 border-b flex gap-3">
                      <button
                        onClick={() => setDocsTarget({ level: 'section', entity: s, title: `Section: ${s.name}` })}
                        className="text-xs text-indigo-700 hover:underline inline-flex items-center gap-1"
                        data-testid={`docs-section-${s.id}`}
                      >
                        <Paperclip size={11} /> Docs
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => { setEditingSection(s); setSectionForm({ name: s.name, description: s.description || '' }); setShowSectionForm(true); }}
                            className="text-xs text-blue-700 hover:underline"
                            data-testid={`edit-section-${s.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSection(s)}
                            className="text-xs text-red-600 hover:underline"
                            data-testid={`delete-section-${s.id}`}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Items */}
        <section className="md:col-span-3">
          {selectedSection ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                  {sections.find(s => s.id === selectedSection)?.name} — Items
                </h2>
                {isAdmin && (
                  <button
                    onClick={() => { setEditingItem(null); setShowItemModal(true); }}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    data-testid="add-item-btn"
                  >
                    <Plus size={16} className="mr-1" /> Add Item
                  </button>
                )}
              </div>

              {isAdmin && (
                <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                  <p className="text-xs text-gray-600 mb-2">Bulk-import items into any section via CSV/XLSX. Unknown Section Names will be auto-created.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                      className="text-sm border rounded px-2 py-1"
                      data-testid="import-items-file"
                    />
                    <button
                      onClick={handleImport}
                      disabled={!importFile || importing}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                      data-testid="import-items-submit"
                    >
                      {importing ? <Loader size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                      {importing ? 'Importing…' : 'Import'}
                    </button>
                    {importResult && (
                      <span className="text-xs text-gray-700 ml-2">
                        Created {importResult.created} · Skipped {importResult.skipped}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-white border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Name</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Type</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Frequency / Trigger</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Due Date</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Assignee</th>
                      <th className="text-center px-4 py-2 font-medium text-gray-700">Docs</th>
                      {isAdmin && <th className="text-right px-4 py-2 font-medium text-gray-700">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 ? (
                      <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-6 text-center text-gray-500">
                        No items in this section yet.
                      </td></tr>
                    ) : (
                      filteredItems.map(it => (
                        <tr key={it.id} className="border-t hover:bg-gray-50" data-testid={`item-row-${it.id}`}>
                          <td className="px-4 py-2 font-medium text-gray-900">{it.name}</td>
                          <td className="px-4 py-2 capitalize">{(it.compliance_type || '').replace('_', '-')}</td>
                          <td className="px-4 py-2 text-gray-600">
                            {it.compliance_type === 'recurring' ? (it.frequency || '') : (it.event_trigger || (it.compliance_type === 'one_time' ? 'One-time' : ''))}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{it.due_date || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{it.default_assignee_name || '—'}</td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => setDocsTarget({ level: 'item', entity: it, title: `Item: ${it.name}` })}
                              className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900"
                              data-testid={`docs-item-${it.id}`}
                              title="Reference documents"
                            >
                              <Paperclip size={14} />
                              {(it.documents || []).length > 0 && (
                                <span className="text-[10px] bg-indigo-50 border border-indigo-200 rounded-full px-1.5"
                                      data-testid={`item-doc-count-${it.id}`}>
                                  {it.documents.length}
                                </span>
                              )}
                            </button>
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => { setEditingItem(it); setShowItemModal(true); }}
                                className="text-blue-600 hover:text-blue-800 mr-3"
                                data-testid={`edit-item-${it.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(it)}
                                className="text-red-600 hover:text-red-800"
                                data-testid={`delete-item-${it.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="bg-white border rounded-lg p-8 text-center text-gray-500">
              {sections.length === 0
                ? 'Create your first section to start adding compliance line items.'
                : 'Pick a section from the left.'}
            </div>
          )}
        </section>
      </div>

      {/* Section form modal */}
      {showSectionForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="section-form-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold">{editingSection ? 'Edit Section' : 'New Section'}</h3>
              <button onClick={() => setShowSectionForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={sectionForm.name}
                  onChange={e => setSectionForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="e.g. SEBI AIF Compliance"
                  data-testid="section-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={sectionForm.description}
                  onChange={e => setSectionForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={3}
                  data-testid="section-desc-input"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
              <button onClick={() => setShowSectionForm(false)} className="px-4 py-2 border rounded text-sm">Cancel</button>
              <button onClick={handleSaveSection} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700" data-testid="save-section-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {showItemModal && (
        <ComplianceItemModal
          open={showItemModal}
          onClose={() => setShowItemModal(false)}
          item={editingItem}
          defaultSectionId={selectedSection}
          sections={sections}
          onSaved={fetchAll}
        />
      )}

      {docsTarget && (
        <ComplianceDocsModal
          level={docsTarget.level}
          entityId={docsTarget.entity.id}
          title={docsTarget.title}
          documents={docsTarget.entity.documents || []}
          isAdmin={isAdmin}
          onClose={() => setDocsTarget(null)}
          onChanged={async () => {
            const fresh = await fetchAll();
            // Refresh the modal's document list from the freshly-fetched data
            const pool = docsTarget.level === 'section' ? fresh.sections : fresh.items;
            const updated = pool.find(x => x.id === docsTarget.entity.id);
            if (updated) setDocsTarget({ ...docsTarget, entity: updated });
          }}
        />
      )}
    </div>
  );
};

export default ComplianceDatabase;
